import { useTranslate } from "@tolgee/react";
import { useAtom } from "jotai";
import { type ReactNode, useEffect, useState } from "react";
import {
	Dialog,
	DialogCloseButton,
	DialogContent,
	DialogTitle,
	DialogTrigger,
} from "#/components/ui/dialog.tsx";
import {
	FlowIndicator,
	type FlowStatus,
	type FlowStep,
	FlowStepPanel,
	type StepStatus,
} from "#/features/trade/components/order-form/fund-flow-ui.tsx";
import { useNeedsDepositToTrading } from "#/hooks/use-needs-deposit-to-trading";
import { useTradingBalance } from "#/hooks/use-trading-balance";
import { useWallet } from "#/hooks/use-wallet";
import { useWalletBalance } from "#/hooks/use-wallet-balance";
import { analytics } from "#/lib/analytics";
import { formatSize, formatUsd } from "#/lib/formatters";
import { StepDepositUSDC } from "./deposit/step-deposit-usdc";
import { StepTransferCrypto } from "./deposit/step-transfer-crypto";
import {
	CompletionPreview,
	StepDone,
	TokenChip,
	truncateAddress,
} from "./shared-fund-flow";
import {
	PENDING_DEPOSIT_COMPLETED_VISIBLE_MS,
	pendingDepositArrived,
	pendingDepositExpired,
	pendingDepositInTransit,
	pendingDepositJustCompleted,
	pendingDepositsAtom,
} from "./stores/pending-deposit";

type DepositDialogProps = {
	children: ReactNode;
	onConfirm?:
		| ((
				amount: number,
		  ) => Promise<{ hash?: string; transactionHash?: string } | undefined>)
		| ((amount: number) => Promise<void>);
};

export function DepositDialog({ children, onConfirm }: DepositDialogProps) {
	const { t } = useTranslate();
	const [open, setOpen] = useState(false);
	const [isPending, setIsPending] = useState(false);
	const [success, setSuccess] = useState(false);
	const [txHash, setTxHash] = useState<string | null>(null);
	const [expandedStep, setExpandedStep] = useState<number | null>(1);
	const { balance: walletBalance, usdtBalance } = useWalletBalance();
	const { address } = useWallet();
	const needsBridgeToTrading = useNeedsDepositToTrading();
	const tradingBalance = useTradingBalance();
	const [pendingMap, setPendingMap] = useAtom(pendingDepositsAtom);

	const addrKey = address?.toLowerCase() ?? "";
	const pendingRecord = addrKey ? pendingMap[addrKey] : undefined;
	// Persisted in-flight bridge deposit (survives dialog close / refresh).
	// "In transit" (crediting) until the trading balance rises above the
	// submit-time baseline — see stores/pending-deposit.ts.
	const now = Date.now();
	const depositInTransit = pendingDepositInTransit(
		pendingRecord,
		tradingBalance,
		now,
	);
	const depositJustCompleted = pendingDepositJustCompleted(pendingRecord, now);
	// Fully landed: accepted this session OR reopened during the 10s
	// "completed" check window (success resets on reopen, the stamped record
	// does not). Without the record check, reopening mid-window showed the
	// deposit form again — with the MAX prefill still reading the pre-deposit
	// wallet balance (30s poll), inviting an accidental duplicate deposit.
	const depositComplete =
		(success || depositJustCompleted) && !depositInTransit;
	// Occupy Step 2's terminal panel while crediting or completed.
	const showCompletion = depositInTransit || depositComplete;

	function handleOpenChange(nextOpen: boolean) {
		if (isPending) return;
		setOpen(nextOpen);
		if (nextOpen) {
			analytics.track("deposit_dialog_opened", {});
			setSuccess(false);
			// Resume an in-flight deposit's "crediting" state — depositInTransit is
			// derived from the persisted record, so just restore the tx hash for the
			// Arbiscan link (the record itself keeps the state alive).
			setTxHash(pendingRecord?.txHash ?? null);
		}
	}

	const activeStep =
		success ||
		depositInTransit ||
		depositJustCompleted ||
		walletBalance > 0 ||
		isPending
			? 2
			: 1;

	// Pending-record lifecycle (sole writer besides onSuccess; runs even while
	// closed — the dialog wrapper stays mounted, often as several instances, so
	// every write is guarded to make duplicates no-ops): expired records are
	// dropped; on arrival the record is stamped with `completedAt` instead of
	// deleted, starting the "completed" check window DepositWarningIndicator
	// shows; the timer then drops the record — that atom write is what
	// re-renders every reader to hide the check.
	useEffect(() => {
		if (!pendingRecord || !addrKey) return;
		const dropRecord = () =>
			setPendingMap((map) => {
				if (!(addrKey in map)) return map;
				const next = { ...map };
				delete next[addrKey];
				return next;
			});
		if (pendingDepositExpired(pendingRecord, Date.now())) {
			dropRecord();
			return;
		}
		if (!pendingRecord.completedAt) {
			if (pendingDepositArrived(pendingRecord, tradingBalance)) {
				setPendingMap((map) => {
					const record = map[addrKey];
					if (!record || record.completedAt) return map;
					return { ...map, [addrKey]: { ...record, completedAt: Date.now() } };
				});
			}
			return;
		}
		const remaining =
			pendingRecord.completedAt +
			PENDING_DEPOSIT_COMPLETED_VISIBLE_MS -
			Date.now();
		if (remaining <= 0) {
			dropRecord();
			return;
		}
		const timeoutId = window.setTimeout(dropRecord, remaining);
		return () => window.clearTimeout(timeoutId);
	}, [pendingRecord, tradingBalance, addrKey, setPendingMap]);

	useEffect(() => {
		if (!open) return;
		setExpandedStep(activeStep);
	}, [activeStep, open]);

	const stepStatuses: Record<number, StepStatus> = {
		1: activeStep > 1 ? "completed" : "active",
		2: depositComplete ? "completed" : activeStep === 2 ? "active" : "upcoming",
	};
	const flowStatuses: Record<FlowStep["id"], FlowStatus> = depositComplete
		? {
				source: "origin",
				"deposit-wallet": "completed",
				"trading-account": "completed",
				complete: "active",
			}
		: activeStep === 2
			? {
					source: "origin",
					"deposit-wallet": "completed",
					"trading-account": "active",
					complete: "upcoming",
				}
			: {
					source: "origin",
					"deposit-wallet": "active",
					"trading-account": "upcoming",
					complete: "upcoming",
				};
	const flowSteps: Array<FlowStep> = [
		{
			id: "source",
			label: t("deposit.flow.source.label"),
			description: t("deposit.flow.source.description"),
			status: flowStatuses.source,
		},
		{
			id: "deposit-wallet",
			label: t("deposit.flow.deposit-wallet.label"),
			description: t("deposit.flow.deposit-wallet.description"),
			status: flowStatuses["deposit-wallet"],
		},
		{
			id: "trading-account",
			label: t("deposit.flow.trading-account.label"),
			description: t("deposit.flow.trading-account.description"),
			status: flowStatuses["trading-account"],
		},
		{
			id: "complete",
			label: t("deposit.flow.complete.label"),
			description: t("deposit.flow.complete.description"),
			status: flowStatuses.complete,
		},
	];

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>{children}</DialogTrigger>
			<DialogContent className="flex max-h-[calc(100dvh-1rem)] w-[min(480px,calc(100vw-1rem))] flex-col overflow-hidden rounded-[22px] border border-trade-border-subtle bg-[linear-gradient(180deg,var(--trade-surface-overlay),var(--trade-surface-overlay-end))] p-0 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.03),0_36px_72px_rgba(0,0,0,0.52)] sm:max-h-[calc(100dvh-2rem)] sm:w-[min(480px,calc(100vw-2rem))]">
				<div className="flex items-start justify-between gap-4 px-5 pt-5">
					<div className="min-w-0">
						<DialogTitle className="text-title1 text-trade-text-strong">
							{t("actions.deposit")}
						</DialogTitle>
						<p className="mt-1 text-label2-medium text-trade-text-secondary">
							{t("form.asset-network")}
						</p>
						<div className="mt-3">
							<FlowIndicator steps={flowSteps} />
						</div>
					</div>
					<DialogCloseButton disabled={isPending} />
				</div>

				<div className="min-h-0 overflow-y-auto px-5 pb-5 pt-4">
					<div className="flex flex-col gap-3">
						<FlowStepPanel
							step={1}
							title={t("deposit.title-step1")}
							status={stepStatuses[1]}
							expanded={expandedStep === 1}
							onToggle={() => setExpandedStep((c) => (c === 1 ? null : 1))}
							preview={<TransferPreview address={address} />}
						>
							<StepTransferCrypto address={address} />
						</FlowStepPanel>

						<FlowStepPanel
							step={2}
							title={
								depositComplete
									? t("deposit.title-step3")
									: depositInTransit
										? t("deposit.crediting-title")
										: t("deposit.title-step2")
							}
							status={stepStatuses[2]}
							expanded={expandedStep === 2}
							onToggle={() =>
								setExpandedStep((c) =>
									c === 2 ? (showCompletion ? 2 : null) : 2,
								)
							}
							preview={
								showCompletion ? (
									<CompletionPreview
										successMessage={
											depositInTransit
												? t("deposit.crediting-message")
												: t("deposit.complete-message")
										}
										txHash={txHash}
									/>
								) : (
									<DepositPreview walletBalance={walletBalance} />
								)
							}
						>
							{showCompletion ? (
								<StepDone
									onClose={() => setOpen(false)}
									pending={depositInTransit}
									successMessage={
										depositInTransit
											? t("deposit.crediting-message")
											: t("deposit.complete-message")
									}
									titleKey={
										depositInTransit
											? "deposit.crediting-title"
											: "deposit.title-step3"
									}
									txHash={txHash}
								/>
							) : (
								<StepDepositUSDC
									walletBalance={walletBalance}
									usdtBalance={usdtBalance}
									isPending={isPending}
									readOnly={stepStatuses[2] !== "active"}
									needsBridgeToTrading={needsBridgeToTrading}
									setIsPending={setIsPending}
									onConfirm={onConfirm}
									onSuccess={(nextTxHash, amount) => {
										setTxHash(nextTxHash);
										setSuccess(true);
										// Persist the in-flight deposit so the "crediting" state
										// survives dialog close / refresh. Snapshot the trading
										// balance as the arrival baseline; when it rises (main-sync
										// WS), the cleanup effect drops the record → "complete".
										if (addrKey) {
											setPendingMap((map) => ({
												...map,
												[addrKey]: {
													amount,
													balanceBefore: tradingBalance,
													ts: Date.now(),
													txHash: nextTxHash,
												},
											}));
										}
									}}
								/>
							)}
						</FlowStepPanel>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}

function TransferPreview({ address }: { address?: string | null }) {
	return (
		<div className="flex flex-wrap items-center gap-2">
			<TokenChip
				iconSrc="https://sdk-cdn.fun.xyz/images/usdc.svg"
				label="USDC"
			/>
			<TokenChip
				iconSrc="https://sdk-cdn.fun.xyz/images/arbitrum.svg"
				label="Arbitrum"
			/>
			{address && (
				<span className="truncate rounded-full bg-trade-surface-inset px-2 py-1 text-caption1-medium text-trade-text-secondary">
					{truncateAddress(address)}
				</span>
			)}
		</div>
	);
}

function DepositPreview({ walletBalance }: { walletBalance: number }) {
	return (
		<div className="flex flex-wrap items-center gap-2">
			<TokenChip
				iconSrc="https://sdk-cdn.fun.xyz/images/usdc.svg"
				label="USDC"
			/>
			<span className="text-caption1-medium text-trade-text-strong">
				{formatSize(walletBalance, 2)} USDC
			</span>
			<span>{formatUsd(walletBalance)}</span>
		</div>
	);
}
